import { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useLanguage } from "@/lib/i18n";
import { thumbUrl, srcSet } from "@/lib/images";

// Pfeil-Cursor für die Klickhälften (Chevron in Knochenfarbe, je nach Seite)
const CURSOR_LEFT =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32'%3E%3Cpath d='M20 7 L11 16 L20 25' fill='none' stroke='%23F4EFE9' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\") 16 16, w-resize";
const CURSOR_RIGHT =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32'%3E%3Cpath d='M12 7 L21 16 L12 25' fill='none' stroke='%23F4EFE9' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\") 16 16, e-resize";

// Gemeinsame Slideshow für Rubrik- und Serien-Seiten.
// Desktop: Unterschrift links unter dem Bild (bündig mit der linken
// Bildkante), Pfeile in derselben Zeile rechts bündig, die Zeile ist so
// breit wie das Bild. Klickhälften auf dem Bild mit Pfeil-Cursor.
// Mobil: Bild fast volle Breite, Unterschrift darunter links, keine
// Pfeile, Wischen. Kein Zähler. Am ersten Bild kein Zurück-Pfeil, nach
// dem letzten Bild führt „vor" zurück zur Übersicht.
export const Slideshow = ({ images, index, onIndexChange, onOverview, overviewLabel }) => {
  const { t } = useLanguage();
  const total = images.length;
  const current = images[index];
  const isLast = index >= total - 1;

  const goPrev = () => {
    if (index > 0) onIndexChange(index - 1);
  };
  const goNext = () => {
    if (isLast) onOverview();
    else onIndexChange(index + 1);
  };

  // Pfeiltasten blättern, Esc zurück zur Übersicht
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "Escape") onOverview();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // Wischen auf Touch-Geräten
  const touchX = useRef(null);
  const onTouchStart = (e) => {
    touchX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) (dx < 0 ? goNext : goPrev)();
    touchX.current = null;
  };

  // Nachbar-Bilder still vorladen (gleiche Breiten wie das Hauptbild)
  useEffect(() => {
    if (total < 2) return;
    [index + 1, index - 1].forEach((i) => {
      const img = images[(i + total) % total];
      if (!img || (i >= total && isLast)) return;
      const im = new Image();
      const set = srcSet(img.image, [800, 1200, 1400]);
      if (set) {
        im.srcset = set;
        im.sizes = "(max-width: 768px) 100vw, 74vw";
      }
      im.src = thumbUrl(img.image, 1400);
    });
  }, [index, images, total, isLast]);

  const land = current.orientation === "landscape";

  return (
    <>
      <div className="mb-4 flex flex-none justify-end">
        <button
          type="button"
          onClick={onOverview}
          data-testid="slideshow-overview-toggle"
          className="font-mono text-[11px] uppercase tracking-[0.25em] text-smoke transition-colors duration-300 hover:text-ember-deep"
        >
          {overviewLabel}
        </button>
      </div>
      <div className="flex justify-center" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        {/* Der Rahmen schnurrt auf die Bildbreite, damit Unterschrift und
            Pfeile exakt mit den Bildkanten bündig stehen */}
        <div className={`w-fit max-w-full ${land ? "md:max-w-[74%]" : "md:max-w-[56%]"}`}>
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.img
                key={current.key}
                src={thumbUrl(current.image, 1400)}
                srcSet={srcSet(current.image, [800, 1200, 1400])}
                sizes={land ? "(max-width: 768px) 100vw, 74vw" : "(max-width: 768px) 100vw, 56vw"}
                alt={current.alt}
                data-testid="slideshow-image"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                className={`block max-h-[calc(100dvh-15rem)] w-auto max-w-full object-contain ${
                  land ? "md:max-h-[calc(100dvh-14rem)]" : "md:max-h-[calc(100dvh-19rem)]"
                }`}
              />
            </AnimatePresence>
            {/* Klickhälften mit Pfeil-Cursor (nur Desktop, mobil wird gewischt) */}
            <button
              type="button"
              aria-label={t("Vorheriges Bild")}
              data-testid="slideshow-prev"
              onClick={goPrev}
              className="absolute inset-y-0 left-0 hidden w-1/2 md:block"
              style={{ cursor: CURSOR_LEFT }}
            />
            <button
              type="button"
              aria-label={t("Nächstes Bild")}
              data-testid="slideshow-next"
              onClick={goNext}
              className="absolute inset-y-0 right-0 hidden w-1/2 md:block"
              style={{ cursor: CURSOR_RIGHT }}
            />
          </div>
          <div className="mt-3 flex w-full items-baseline justify-between gap-4">
            <p
              className="min-w-0 font-mono text-[10px] uppercase tracking-[0.2em] text-bone"
              data-testid="slideshow-caption"
            >
              {current.title}
              {current.ort && <span className="normal-case text-smoke"> · {current.ort}</span>}
            </p>
            <div className="hidden flex-none items-center gap-6 md:flex" data-testid="slideshow-nav">
              {index > 0 && (
                <button
                  type="button"
                  aria-label={t("Vorheriges Bild")}
                  data-testid="slideshow-arrow-prev"
                  onClick={goPrev}
                  className="py-2 text-smoke transition-colors duration-300 hover:text-ember-deep"
                >
                  <ArrowLeft size={16} strokeWidth={1.5} />
                </button>
              )}
              <button
                type="button"
                aria-label={t("Nächstes Bild")}
                data-testid="slideshow-arrow-next"
                onClick={goNext}
                className="py-2 text-smoke transition-colors duration-300 hover:text-ember-deep"
              >
                <ArrowRight size={16} strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
